import { useEffect, useRef, useState } from "react";
import { FLAVORS } from "@/lib/catalog";

export default function Gallery() {
  const wrapperRef = useRef(null);
  const [progress, setProgress] = useState(0); // 0..1 across the reveal range

  useEffect(() => {
    const onScroll = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // Reveal range = scroll length minus one viewport (the sticky child).
      const totalScroll = el.offsetHeight - vh;
      if (totalScroll <= 0) return;
      const scrolled = Math.min(Math.max(-rect.top, 0), totalScroll);
      setProgress(scrolled / totalScroll);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Map progress (0..1) to "revealed count" (0..FLAVORS.length).
  // Use an early start so cards begin appearing as soon as the header is visible.
  const totalSteps = FLAVORS.length;
  const eased = Math.min(1, Math.max(0, (progress - 0.05) / 0.85));
  const revealedFloat = eased * totalSteps;

  return (
    <section
      id="galeria"
      data-testid="gallery-section"
      ref={wrapperRef}
      className="relative border-y border-black bg-white"
      style={{ height: "260vh" }}
    >
      <div className="sticky top-0 h-screen flex flex-col">
        <div className="mx-auto max-w-[1400px] w-full px-6 md:px-10 pt-12 md:pt-16">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em]">
                <span className="inline-block w-10 h-px bg-black" />
                <span>Collab · A Confiteria</span>
              </div>
              <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.02em]">
                O recheio<br />que assina cada brownie.
              </h2>
            </div>
            <p className="max-w-md text-sm md:text-base leading-relaxed">
              Quatro dos cinco sabores nasceram de uma collab com{" "}
              <span className="font-medium">A Confiteria</span> — recheios fit, sem
              adição de açúcar, sem glúten e sem lactose. O{" "}
              <span className="font-medium">Beauty Protein</span> é nosso clássico
              tradicional, sem collab.
            </p>
          </div>
        </div>

        <div className="flex-1 flex items-center">
          <ul
            data-testid="gallery-row"
            className="grid grid-cols-5 gap-0 border-t border-l border-black w-full mx-auto max-w-[1400px]"
          >
            {FLAVORS.map((f, i) => {
              const localProgress = Math.min(1, Math.max(0, revealedFloat - i));
              const isVisible = localProgress > 0;
              const transform = `translateX(${(1 - localProgress) * 24}px)`;
              return (
                <li
                  key={f.slug}
                  data-testid={`gallery-${f.slug}`}
                  data-revealed={isVisible ? "true" : "false"}
                  className="relative aspect-square border-r border-b border-black overflow-hidden bg-white"
                  style={{
                    opacity: localProgress,
                    transform,
                    transition: "opacity 200ms linear, transform 300ms ease-out",
                  }}
                >
                  <img
                    src={f.img}
                    alt={f.label}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="eager"
                  />
                  {!f.collab && (
                    <span className="absolute right-2 top-2 text-[9px] md:text-[10px] uppercase tracking-[0.2em] border border-black bg-white px-2 py-1">
                      Tradicional
                    </span>
                  )}
                  <div className="absolute left-2 bottom-2 right-2">
                    <span className="font-display text-xs md:text-base bg-white/95 px-2 py-1 border border-black inline-block">
                      {f.label}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="mx-auto max-w-[1400px] w-full px-6 md:px-10 pb-8 md:pb-10">
          <div className="flex items-center justify-between text-[10px] md:text-xs uppercase tracking-[0.2em]">
            <span>{Math.min(totalSteps, Math.floor(revealedFloat))}/{totalSteps} revelados</span>
            <span>Continue rolando ↓</span>
          </div>
          <div className="mt-3 h-px bg-black/10 relative">
            <div
              className="absolute left-0 top-0 h-px bg-black"
              style={{ width: `${Math.min(100, eased * 100)}%`, transition: "width 120ms linear" }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
